'use client';

import { useMemo } from 'react';
import useSWR from 'swr';
import { useLocation } from 'react-router-dom';
import { useUser } from '@/hooks/use-user';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CommunityMyBusinessLinks } from '@/components/community/community-my-business-links';
import axiosInstance from '@/lib/axios';
import type { Community, UserWithCommunities } from '@/types';

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

type CommunityMembership = UserWithCommunities['communities'][number];

/**
 * When curated listings apply and min and/or max per member is configured, blocks navigation
 * until the member is within limits (same pattern as {@link OnboardingPrompt}).
 * Applies to members whose status is JOINED or PENDING for the URL community.
 */
export function BusinessLinkCompliancePrompt() {
  const { user, isLoading } = useUser();
  const location = useLocation();

  const isOnCreateBusinessFlow = location.pathname.includes('/create/business');

  const currentCommunityId = useMemo(() => {
    const m = location.pathname.match(/^\/([^/]+)/);
    return m ? m[1] : null;
  }, [location.pathname]);

  const membership = useMemo((): CommunityMembership | undefined => {
    if (!user?.communities || !currentCommunityId) return undefined;
    return user.communities.find(
      (c) => c.id === currentCommunityId && (c.status === 'JOINED' || c.status === 'PENDING')
    );
  }, [user, currentCommunityId]);

  /** Authoritative config (same as settings / layout). `user.communities` can lag or omit config fields. */
  const communityDetailKey =
    !isLoading && user && membership && currentCommunityId
      ? `/api/communities/${currentCommunityId}`
      : null;

  const { data: communityBundle } = useSWR<{ community: Community }>(communityDetailKey, fetcher);

  const communityFromApi = communityBundle?.community;

  const minRequired = useMemo(() => {
    const c = communityFromApi;
    if (!c?.controlledBusinessListing || c.businessLinksMinPerMember == null) return null;
    const n = Number(c.businessLinksMinPerMember);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [communityFromApi]);

  /** Null means no max cap (unlimited). */
  const maxAllowed = useMemo(() => {
    const c = communityFromApi;
    if (!c?.controlledBusinessListing || c.businessLinksMaxPerMember == null) return null;
    const n = Number(c.businessLinksMaxPerMember);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [communityFromApi]);

  const hasMinOrMaxLimit = minRequired != null || maxAllowed != null;

  /** Not gated on `isOnboarded` — compliance modal can show in parallel with onboarding. */
  const businessLinksSwrKey =
    !isLoading &&
    user &&
    membership &&
    currentCommunityId &&
    communityFromApi?.controlledBusinessListing === true &&
    hasMinOrMaxLimit
      ? `/api/communities/${currentCommunityId}/business-links`
      : null;

  const { data: linksData } = useSWR(businessLinksSwrKey, fetcher);

  const active = linksData?.limits?.activeApprovedOrPending;

  const underMin = minRequired != null && typeof active === 'number' && active < minRequired;
  const overMax = maxAllowed != null && typeof active === 'number' && active > maxAllowed;

  const needsCompliance = underMin || overMax;

  const shouldShow =
    !isOnCreateBusinessFlow && Boolean(businessLinksSwrKey) && linksData != null && needsCompliance;

  const communityForChild = useMemo((): Community | undefined => {
    if (!communityFromApi || !membership) return undefined;
    return { ...membership, ...communityFromApi };
  }, [communityFromApi, membership]);

  const activeCount = typeof active === 'number' ? active : 0;
  const isNewUserNoBusiness = (user?.businesses?.length ?? 0) === 0;

  if (!membership || !communityForChild || !hasMinOrMaxLimit) {
    return null;
  }

  return (
    <Dialog open={shouldShow}>
      <DialogContent
        className="max-w-lg gap-0 overflow-hidden p-0 sm:max-w-xl"
        hideCloseButton
        onPointerDownOutside={(e) => {
          if (shouldShow) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (shouldShow) e.preventDefault();
        }}
      >
        <DialogHeader
          className={
            isNewUserNoBusiness
              ? 'space-y-3 px-6 pt-6 pb-2'
              : 'border-b border-foreground/25 bg-muted/30 p-6 pb-4 space-y-3'
          }
        >
          <DialogTitle className="text-xl font-bold">
            {overMax ? 'Remove a listing' : 'Add your business'}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3 text-sm leading-relaxed">
              {overMax && maxAllowed != null ? (
                <>
                  <p className="text-foreground">
                    You can list at most {maxAllowed} business{maxAllowed === 1 ? '' : 'es'}. Remove
                    one below to continue.
                  </p>
                  <p className="inline-flex rounded-full bg-background px-3 py-1 text-xs font-semibold border text-red-600 border-red-500">
                    {activeCount} of {maxAllowed} used
                  </p>
                </>
              ) : minRequired != null ? (
                <>
                  <p className="text-foreground">
                    This community needs at least {minRequired} business listing
                    {minRequired === 1 ? '' : 's'}.
                  </p>
                  <p className="inline-flex rounded-full bg-background px-3 py-1 text-xs font-semibold text-foreground border border-foreground/40">
                    {activeCount} of {minRequired} added
                  </p>
                </>
              ) : (
                <p className="text-foreground">Adjust your listings below.</p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <div
          className={
            isNewUserNoBusiness ? 'px-6 pb-6 pt-4' : 'max-h-[min(70vh,560px)] overflow-y-auto p-4'
          }
        >
          <CommunityMyBusinessLinks community={communityForChild} presentation="compact" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
